import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/lib/supabase';

interface MFAVerificationProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function MFAVerification({ onSuccess, onCancel }: MFAVerificationProps) {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError(t('settings.2fa.codeLength') || 'Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Get the list of factors
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) throw factorsError;

      const totpFactor = factorsData.totp.find((f) => f.status === 'verified');
      if (!totpFactor) {
        throw new Error('No verified TOTP factor found');
      }

      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });
      if (challengeError) throw challengeError;

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) {
        throw verifyError;
      }

      onSuccess();
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(t('settings.2fa.invalidCode') || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="w-16 h-16 bg-[#16c47f]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-[#16c47f]" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('settings.2fa.verifyTitle') || 'Two-Factor Authentication Required'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('settings.2fa.verifyDesc') || 'Enter the code from your authenticator app'}
        </p>
      </div>

      <div className="space-y-2">
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          value={verificationCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '');
            setVerificationCode(value);
            setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && verificationCode.length === 6) {
              handleVerify();
            }
          }}
          placeholder="000000"
          className="text-center text-2xl tracking-widest font-mono h-14 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-xl"
          autoFocus
        />
        {error && (
          <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1 h-12 rounded-xl dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          disabled={isLoading}
        >
          {t('settings.2fa.back') || 'Back'}
        </Button>
        <Button
          onClick={handleVerify}
          disabled={isLoading || verificationCode.length !== 6}
          className="flex-1 h-12 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium rounded-xl"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            t('settings.2fa.verify') || 'Verify'
          )}
        </Button>
      </div>
    </motion.div>
  );
}
